import React, { useRef } from 'react';

import { RefreshTokenRequest } from 'expo-auth-session';
import * as SecureStore from 'expo-secure-store';
import jwtDecode from 'jwt-decode';
import { Platform } from 'react-native';

import {
	ApolloClient,
	createHttpLink,
	InMemoryCache,
	NormalizedCacheObject,
	split,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { WebSocketLink } from '@apollo/client/link/ws';
import {
	getMainDefinition,
	Reference,
} from '@apollo/client/utilities';
import AsyncStorage from '@react-native-async-storage/async-storage';

import {
	useAppState,
	useAppStateRecoil,
} from '../helpers';
import { authAtom } from '../state';

export interface JnttnApolloClientOptions {
	clientId: string;
	endpoint: string;
	endpointWS: string;
	scopes: string[];
	applicationId?: 'portfolio-web' | 'maestro';
	applicationVersion?: string;
}

function mergeNodes(existing: any, incoming: any) {
	let mergedNodes: Reference[] = [];

	// queries with same endcursors are duplicates, dont merge.
	// we must...figure out better pattern though.
	if (existing?.pageInfo?.endCursor === incoming?.pageInfo?.endCursor) {
		return existing;
	}

	if (existing && existing.nodes) {
		mergedNodes = mergedNodes.concat(existing.nodes);
	}

	if (incoming && incoming.nodes) {
		mergedNodes = mergedNodes.concat(incoming.nodes);
	}

	return {
		...incoming,
		nodes: mergedNodes,
	};
}

export const cache: InMemoryCache = new InMemoryCache({
	typePolicies: {
		Query: {
			fields: {
				marketOrders: {
					keyArgs: false,
					merge: mergeNodes,
				},
				fundings: {
					keyArgs: false,
					merge: mergeNodes,
				},
				stakingEnrollments: {
					keyArgs: false,
					merge: mergeNodes,
				},
				conditionalTransactions: {
					keyArgs: false,
					merge: mergeNodes,
				},
				currency: {
					merge: true,
				},
			},
		},
	},
});

function useSingleFlight<T>(fn: () => Promise<T>) {
	const inFlightRef = useRef<Promise<T> | null>(null);

	const run = () => {
		if (!inFlightRef.current) {
			inFlightRef.current = fn().finally(() => {
				inFlightRef.current = null;
			});
		}

		return inFlightRef.current;
	};

	return run;
}

export function useAppApolloClient(options: JnttnApolloClientOptions) {
	const authState = useAppStateRecoil(authAtom);

	const fetchAccessToken = useSingleFlight(async () => {
		let accessToken: string | null;
		let refreshToken: string | null;

		if (Platform.OS === 'web') {
			accessToken = await AsyncStorage.getItem('accessToken');
			refreshToken = await AsyncStorage.getItem('refreshToken');
		} else {
			accessToken = await SecureStore.getItemAsync('accessToken');
			refreshToken = await SecureStore.getItemAsync('refreshToken');
		}

		if (!accessToken || !refreshToken) return null;

		const { exp }: any = jwtDecode(accessToken);
		const isExpired = Date.now() >= exp * 1000;

		if (isExpired) {
			try {
				const request = new RefreshTokenRequest({
					clientId: options.clientId,
					scopes: options.scopes,
					refreshToken: refreshToken ?? '',
				});

				const tokenResult = await request.performAsync(authState.get.discovery!);

				if (Platform.OS === 'web') {
					await AsyncStorage.setItem('accessToken', tokenResult.accessToken);
					await AsyncStorage.setItem('refreshToken', tokenResult.refreshToken!);
				} else {
					await SecureStore.setItemAsync('accessToken', tokenResult.accessToken);
					await SecureStore.setItemAsync('refreshToken', tokenResult.refreshToken!);
				}

				return tokenResult.accessToken;
			} catch (e) {
				authState.get.onRefreshTokenExpired?.();
				return null;
			}
		}

		return accessToken;
	});

	const client = useAppState<ApolloClient<NormalizedCacheObject>>(
		new ApolloClient({
			cache,
			queryDeduplication: true,
			link: createHttpLink({
				uri: options.endpoint + '/graphql',
			}),
			defaultOptions: {
				query: {
					notifyOnNetworkStatusChange: true,
				},
				watchQuery: {
					notifyOnNetworkStatusChange: true,
				},
			},
		})
	);

	React.useEffect(() => {
		const httpLink = createHttpLink({
			uri: options.endpoint + '/graphql',
		});

		const wsLink = new WebSocketLink({
			uri: options.endpointWS + '/graphql',
			options: {
				reconnect: false,
			},
		});

		const errorLink = onError(({ graphQLErrors, networkError }) => {
			if (graphQLErrors) {
				graphQLErrors.forEach(({ message, locations, path }) => {
					console.error(
						`[GraphQL error]: Message: ${message}, Location: ${JSON.stringify(
							locations
						)}, Path: ${path}`
					);
				});
			}

			if (networkError) {
				console.error(`[Network error]: ${networkError.message}`);
			}
		});

		const authMiddleware = setContext(async (_, { headers }) => {
			const defaultHeaders = {
				...headers,
				...(options?.applicationId && {
					'x-client': options.applicationId,
				}),
				...(options?.applicationVersion && {
					'x-client-version': options.applicationVersion,
				}),
			};

			const accessToken = await fetchAccessToken();
			if (accessToken) {
				return {
					headers: {
						...defaultHeaders,
						authorization: `Bearer ${accessToken}`,
					},
				};
			} else {
				return {
					headers: {
						...defaultHeaders,
					},
				};
			}
		});

		const splitLink = split(
			({ query }) => {
				const definition = getMainDefinition(query);

				return (
					definition.kind === 'OperationDefinition' &&
					definition.operation === 'subscription'
				);
			},
			wsLink,
			authMiddleware.concat(errorLink.concat(httpLink))
		);

		client.set(
			new ApolloClient({
				cache,
				link: splitLink,
				queryDeduplication: true,
				defaultOptions: {
					query: {
						notifyOnNetworkStatusChange: true,
					},
					watchQuery: {
						notifyOnNetworkStatusChange: true,
					},
				},
			})
		);
	}, [authState.get, options.endpoint]);

	return client.get;
}
